// CR460: Data Source Banner — shows which step attempts are being viewed
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertTriangle, CheckCircle2, HelpCircle, Layers, Zap } from "lucide-react";
import type { StepAttempt, SyncStatus } from "./useStepAttempts";
import { getOcrEngineFromConfig, getOcrEngineLabel } from "@/lib/batch-runs/ocrEngine";

interface DataSourceBannerProps {
  ocrAttempts: StepAttempt[];
  aiAttempts: StepAttempt[];
  selectedOcrAttemptId: string | null;
  selectedAiAttemptId: string | null;
  syncStatus: SyncStatus;
  onSelectOcrAttempt: (id: string) => void;
  onSelectAiAttempt: (id: string) => void;
}

function formatEngine(config: Record<string, unknown> | null): string {
  return getOcrEngineLabel(getOcrEngineFromConfig(config), "Unknown");
}

function formatStatus(status: string): { label: string; variant: "default" | "secondary" | "outline" | "destructive" } {
  switch (status) {
    case "COMPLETE": return { label: "Complete", variant: "default" };
    case "RUNNING": return { label: "Running", variant: "secondary" };
    case "FAILED": return { label: "Failed", variant: "destructive" };
    case "COMPLETE_EMPTY": return { label: "Empty", variant: "outline" };
    case "SKIPPED": return { label: "Skipped", variant: "outline" };
    default: return { label: status, variant: "outline" };
  }
}

function SyncBadge({ status }: { status: SyncStatus }) {
  switch (status) {
    case "IN_SYNC":
      return (
        <Badge variant="outline" className="text-xs bg-green-50 dark:bg-green-900/20 border-green-300 text-green-700 dark:text-green-400 gap-1">
          <CheckCircle2 className="h-3 w-3" />
          In Sync
        </Badge>
      );
    case "OUT_OF_SYNC":
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="text-xs bg-amber-50 dark:bg-amber-900/20 border-amber-300 text-amber-700 dark:text-amber-400 gap-1 cursor-help">
                <AlertTriangle className="h-3 w-3" />
                Out of Sync
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-xs">The Deep Scan was run against a different OCR attempt. Re-run S1.4 to sync.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    case "NO_AI":
      return (
        <Badge variant="outline" className="text-xs gap-1">
          <HelpCircle className="h-3 w-3" />
          No AI Run
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-xs gap-1">
          <HelpCircle className="h-3 w-3" />
          Legacy (unlinked)
        </Badge>
      );
  }
}

function AttemptLabel(attempt: StepAttempt) {
  const st = formatStatus(attempt.status);
  const engine = formatEngine(attempt.config_json);
  const date = attempt.finished_at
    ? new Date(attempt.finished_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : attempt.started_at
      ? "Running..."
      : "Pending";
  return `#${attempt.attempt_no} — ${engine} — ${st.label} — ${date}`;
}

export function DataSourceBanner({
  ocrAttempts,
  aiAttempts,
  selectedOcrAttemptId,
  selectedAiAttemptId,
  syncStatus,
  onSelectOcrAttempt,
  onSelectAiAttempt,
}: DataSourceBannerProps) {
  const selectedOcr = ocrAttempts.find((a) => a.attempt_id === selectedOcrAttemptId);
  const selectedAi = aiAttempts.find((a) => a.attempt_id === selectedAiAttemptId);

  // Don't show if no attempts exist
  if (ocrAttempts.length === 0 && aiAttempts.length === 0) return null;

  return (
    <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Layers className="h-3.5 w-3.5" />
        Data Source
        <SyncBadge status={syncStatus} />
      </div>

      <div className="flex flex-wrap gap-4">
        {/* OCR Attempt Selector */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-medium whitespace-nowrap">OCR+Signals:</span>
          {ocrAttempts.length <= 1 ? (
            <Badge variant="outline" className="text-xs truncate max-w-[280px]">
              {selectedOcr ? AttemptLabel(selectedOcr) : "None"}
            </Badge>
          ) : (
            <Select value={selectedOcrAttemptId ?? ""} onValueChange={onSelectOcrAttempt}>
              <SelectTrigger className="h-7 text-xs w-[280px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ocrAttempts.map((a) => (
                  <SelectItem key={a.attempt_id} value={a.attempt_id} className="text-xs">
                    {AttemptLabel(a)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {selectedOcr && (
            <span className="text-xs text-muted-foreground">
              {selectedOcr.records_out ?? "?"} pages
            </span>
          )}
        </div>

        <Separator orientation="vertical" className="h-5 hidden sm:block" />

        {/* AI Attempt Selector */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-medium whitespace-nowrap flex items-center gap-1">
            <Zap className="h-3 w-3" />
            AI Deep Scan:
          </span>
          {aiAttempts.length === 0 ? (
            <Badge variant="outline" className="text-xs">Not run</Badge>
          ) : aiAttempts.length === 1 ? (
            <Badge variant="outline" className="text-xs truncate max-w-[280px]">
              {selectedAi ? AttemptLabel(selectedAi) : "None"}
            </Badge>
          ) : (
            <Select value={selectedAiAttemptId ?? ""} onValueChange={onSelectAiAttempt}>
              <SelectTrigger className="h-7 text-xs w-[280px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {aiAttempts.map((a) => (
                  <SelectItem key={a.attempt_id} value={a.attempt_id} className="text-xs">
                    {AttemptLabel(a)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {selectedAi && (
            <span className="text-xs text-muted-foreground">
              {selectedAi.records_out ?? "?"} deals
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
